import React, { useState } from "react";
import { Box, Chip, Typography, Popover, Fade, alpha } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CategoryIcon from "@mui/icons-material/Category";
import PublicIcon from "@mui/icons-material/Public";
import LockIcon from "@mui/icons-material/Lock";
import {
  GfFigmaTemplateData,
  GfFlyerItemCategory,
} from "@swagger/typescript-fetch-goflyer";
import {
  NEUTRAL_100,
  NEUTRAL_800,
  PRIMARY_COLOR,
} from "../../app/pages/FIgmaTemplateData/Create-Edit-Page/styles";
import { useTranslation } from "react-i18next";
import { messages } from "../../locales/messages";

export interface TemplateDataCardProps {
  template: GfFigmaTemplateData;
  isSelected?: boolean;
  onSelectToggle?: (tpl: GfFigmaTemplateData) => void;
  onPreview?: (src?: string) => void;
  selectable?: boolean; // controls showing the selection overlay
  elevationActive?: boolean; // keep styling option
}

export const TemplateDataCard: React.FC<TemplateDataCardProps> = ({
  template,
  isSelected = false,
  onSelectToggle,
  onPreview,
  selectable = true,
  elevationActive = true,
}) => {
  const { t } = useTranslation();
  const categories = (template.category || []) as GfFlyerItemCategory[];
  const MAX_VISIBLE_CATEGORIES = 10;
  const initialCategories = categories.slice(0, MAX_VISIBLE_CATEGORIES);
  const hasMoreCategories = categories.length > MAX_VISIBLE_CATEGORIES;
  const [categoryAnchorEl, setCategoryAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [popoverOpenForId, setPopoverOpenForId] = useState<string | null>(null);

  const handleCategoryPopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setCategoryAnchorEl(event.currentTarget);
    setPopoverOpenForId(template.id!);
  };
  const handleCategoryPopoverClose = () => {
    setCategoryAnchorEl(null);
    setPopoverOpenForId(null);
  };
  const isCategoryPopoverOpen =
    popoverOpenForId === template.id && Boolean(categoryAnchorEl);

  const handleRootClick = () => {
    if (selectable && onSelectToggle) {
      onSelectToggle(template);
    }
  };

  return (
    <Box
      onClick={handleRootClick}
      data-testid="template-selection-modal-template-card"
      sx={{
        cursor: selectable ? "pointer" : "default",
        position: "relative",
        p: 2,
        borderRadius: "14px",
        border: `2px solid ${isSelected ? PRIMARY_COLOR : "transparent"}`,
        background: isSelected ? alpha(PRIMARY_COLOR, 0.05) : "#fff",
        boxShadow: elevationActive
          ? isSelected
            ? `0 6px 20px ${alpha(PRIMARY_COLOR, 0.15)}`
            : "0 1px 10px rgba(0,0,0,0.05)"
          : "none",
        transition: "all .25s cubic-bezier(0.2, 0, 0.2, 1)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        "&:hover": selectable
          ? {
              boxShadow: isSelected
                ? `0 8px 24px ${alpha(PRIMARY_COLOR, 0.2)}`
                : "0 6px 16px rgba(0,0,0,0.07)",
              transform: "translateY(-3px)",
            }
          : undefined,
      }}
    >
      {selectable && isSelected && (
        <Fade in={isSelected}>
          <Box
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              zIndex: 2,
              width: 24,
              height: 24,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: PRIMARY_COLOR,
              color: "#fff",
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onSelectToggle && onSelectToggle(template);
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 18 }} />
          </Box>
        </Fade>
      )}

      <Box
        onClick={(e) => {
          e.stopPropagation();
          onPreview && onPreview(template.image?.src);
        }}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 160,
          mb: 1.5,
          overflow: "hidden",
          borderRadius: 2,
          background: `${NEUTRAL_100}`,
          position: "relative",
          "&:hover": {
            "& > img": { transform: "scale(1.05)" },
            "& .preview-overlay": { opacity: 1 },
          },
        }}
      >
        {template.image?.src ? (
          <img
            src={template.image.src}
            alt={template.name}
            data-testid="template-preview-image"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              transition: "transform .4s cubic-bezier(0.2, 0, 0.2, 1)",
            }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t(messages.NoPreview())}
          </Typography>
        )}
        {template.image?.src && (
          <Box
            className="preview-overlay"
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.3)",
              color: "#fff",
              opacity: 0,
              transition: "opacity .3s ease",
              backdropFilter: "blur(2px)",
            }}
          >
            <Typography
              variant="button"
              sx={{
                fontSize: 13,
                fontWeight: 600,
                px: 2,
                py: 0.7,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.25)",
                backdropFilter: "blur(4px)",
              }}
            >
              {t(messages.Preview())}
            </Typography>
          </Box>
        )}
      </Box>

      <Typography
        variant="subtitle1"
        fontWeight={600}
        gutterBottom
        noWrap
        title={template.name}
        data-testid="template-name"
        sx={{ fontSize: 14 }}
      >
        {template.name}
      </Typography>

      {(template.figmaTemplateTag?.name || template.type) && (
        <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {template.figmaTemplateTag?.name && (
            <Chip
              size="small"
              label={template.figmaTemplateTag.name}
              sx={{
                bgcolor: alpha(PRIMARY_COLOR, 0.1),
                color: PRIMARY_COLOR,
                fontWeight: 600,
                fontSize: 10,
                height: 20,
                "& .MuiChip-label": { px: 0.8, py: 0 },
              }}
            />
          )}
          {template.type && (
            <Chip
              size="small"
              variant="outlined"
              icon={
                template.type.toString().toLowerCase() === "specific" ? (
                  <LockIcon sx={{ fontSize: 14 }} />
                ) : (
                  <PublicIcon sx={{ fontSize: 14 }} />
                )
              }
              label={(template.type.toString().toLowerCase() === "specific"
                ? "Specific"
                : "Public"
              ).toUpperCase()}
              sx={{
                borderColor:
                  template.type.toString().toLowerCase() === "specific"
                    ? alpha("#E65100", 0.6)
                    : alpha(PRIMARY_COLOR, 0.6),
                bgcolor:
                  template.type.toString().toLowerCase() === "specific"
                    ? alpha("#E65100", 0.06)
                    : alpha(PRIMARY_COLOR, 0.05),
                color:
                  template.type.toString().toLowerCase() === "specific"
                    ? "#BF360C"
                    : PRIMARY_COLOR,
                fontWeight: 700,
                letterSpacing: 0.4,
                fontSize: 9.5,
                height: 22,
                "& .MuiChip-label": { px: 0.8, py: 0 },
                "& .MuiChip-icon": {
                  ml: 0.2,
                  mr: -0.2,
                  color:
                    template.type.toString().toLowerCase() === "specific"
                      ? "#E65100"
                      : PRIMARY_COLOR,
                },
              }}
            />
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="template-page-count"
          sx={{ fontSize: 12 }}
        >
          {template.numberOfPages || 0} page
          {(template.numberOfPages || 0) > 1 ? "s" : ""}
        </Typography>

        {categories.length > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              color: "text.secondary",
              fontSize: 12,
              gap: 0.5,
              cursor: "pointer",
              "&:hover": { color: "primary.main" },
            }}
            onClick={handleCategoryPopoverOpen}
          >
            <CategoryIcon sx={{ fontSize: 14 }} />
            <Typography
              variant="caption"
              data-testid="template-category-count"
              sx={{ fontWeight: 500 }}
            >
              {categories.length}{" "}
              {categories.length === 1 ? "category" : "categories"}
            </Typography>
          </Box>
        )}
      </Box>

      {categories.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: 0.5,
            position: "relative",
            mt: "auto",
            overflowX: "auto",
            pb: 0.5,
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            whiteSpace: "nowrap",
          }}
        >
          {initialCategories.map((c) => (
            <Chip
              size="small"
              key={c.id}
              label={c.name}
              sx={{
                bgcolor: alpha(PRIMARY_COLOR, 0.08),
                color: NEUTRAL_800,
                fontWeight: 500,
                fontSize: 10,
                height: 20,
                flexShrink: 0,
                "& .MuiChip-label": { px: 0.8, py: 0 },
              }}
            />
          ))}
          {hasMoreCategories && (
            <Chip
              size="small"
              icon={
                <ArrowDropDownIcon style={{ fontSize: 16, marginLeft: 0 }} />
              }
              label={`+${categories.length - MAX_VISIBLE_CATEGORIES}`}
              onClick={handleCategoryPopoverOpen}
              sx={{
                bgcolor: alpha(PRIMARY_COLOR, 0.05),
                color: "primary.main",
                fontWeight: 600,
                fontSize: 10,
                height: 20,
                flexShrink: 0,
                "& .MuiChip-label": { px: 0.5, py: 0, paddingLeft: 0 },
                "& .MuiChip-icon": { marginRight: 0.2 },
                cursor: "pointer",
                "&:hover": { bgcolor: alpha(PRIMARY_COLOR, 0.1) },
              }}
              clickable
            />
          )}

          <Popover
            id={`category-popover-${template.id}`}
            open={isCategoryPopoverOpen}
            anchorEl={categoryAnchorEl}
            onClose={handleCategoryPopoverClose}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                p: 2,
                maxWidth: 300,
                maxHeight: 300,
                overflow: "auto",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                borderRadius: 2,
              },
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              All Categories ({categories.length})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {categories.map((c) => (
                <Chip
                  size="small"
                  key={c.id}
                  label={c.name}
                  sx={{
                    bgcolor: alpha(PRIMARY_COLOR, 0.08),
                    color: NEUTRAL_800,
                    fontWeight: 500,
                    fontSize: 11,
                    height: 22,
                    "& .MuiChip-label": { px: 1 },
                  }}
                />
              ))}
            </Box>
          </Popover>
        </Box>
      )}
    </Box>
  );
};

export default TemplateDataCard;
